import { useEffect, useRef, useState } from "react";
import type { JSX, ReactNode } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  View,
} from "@aws-amplify/ui-react";
import type {
  LocationData,
  StorageBrowserType,
  UseView,
} from "@aws-amplify/ui-react-storage/browser";

import type { CustomerScope } from "@/auth/customerScope";
import {
  folderDisplayName,
  projectDisplayName,
  projectOfPrefix,
} from "../folderNames";
import { BreadcrumbTrail } from "./files";
import { ListingLayout } from "./layout";
import { ListingPagination } from "./pagination";
import type { ProjectSelection } from "./projectSelection";
import { useSelectedProject } from "./projectSelection";

const DEFAULT_PAGE_TITLE = "Home";
const FOLDER_COLUMN_HEADER = "Folder";
const PROJECT_COLUMN_HEADER = "Project";
const FOLDER_ITEM_LABEL = "Folders";
const PROJECT_ITEM_LABEL = "Projects";

/** One row of the listing: a project to open, or a folder to enter. */
type ListingRow = {
  readonly key: string;
  readonly displayName: string;
  readonly onOpen: () => void;
};

/** What the page shows: its title, column and rows. */
type ListingContent = {
  readonly title: ReactNode;
  readonly columnHeader: string;
  readonly itemLabel: string;
  readonly rows: readonly ListingRow[];
};

function matchesFilter(displayName: string, filterText: string): boolean {
  return displayName.toLowerCase().includes(filterText.trim().toLowerCase());
}

/** The part of the Locations view state that says whether there is a choice to make. */
type LocationsListing = {
  readonly hasError: boolean;
  readonly hasNextPage: boolean;
  readonly isLoading: boolean;
  readonly pageItems: readonly LocationData[];
};

/**
 * The one location a listing holds, when choosing from it would be no choice at all.
 */
function soleLocation(listing: LocationsListing): LocationData | undefined {
  const { hasError, hasNextPage, isLoading, pageItems } = listing;
  if (isLoading || hasError || hasNextPage || pageItems.length !== 1) {
    return undefined;
  }
  return pageItems[0];
}

/** A customer's single project heads the page; staff see the default title. */
function pageTitleFor(customerScope: CustomerScope | undefined): string {
  return customerScope
    ? projectDisplayName(customerScope.projects[0])
    : DEFAULT_PAGE_TITLE;
}

function folderRows(
  locations: readonly LocationData[],
  projectPrefixRoot: string,
  onNavigate: (location: LocationData) => void,
): ListingRow[] {
  return locations.map((location) => ({
    key: location.id,
    displayName: folderDisplayName(location.prefix, projectPrefixRoot),
    onOpen: () => {
      onNavigate(location);
    },
  }));
}

/**
 * The project list a multi-project customer starts from.
 */
function projectListContent(
  customerScope: CustomerScope,
  onSelect: (project: string) => void,
): ListingContent {
  return {
    title: projectDisplayName(customerScope.organization),
    columnHeader: PROJECT_COLUMN_HEADER,
    itemLabel: PROJECT_ITEM_LABEL,
    rows: customerScope.projects.map((project) => ({
      key: project,
      displayName: projectDisplayName(project),
      onOpen: () => {
        onSelect(project);
      },
    })),
  };
}

/**
 * One project's folders. Every customer location arrives on the first page, since a
 * customer holds at most 20 projects of three folders and a page holds 100.
 */
function projectFoldersContent(
  customerScope: CustomerScope,
  project: string,
  locations: readonly LocationData[],
  projectPrefixRoot: string,
  onNavigate: (location: LocationData) => void,
  onBackToProjects: () => void,
): ListingContent {
  const projectLocations = locations.filter(
    (location) =>
      projectOfPrefix(location.prefix, projectPrefixRoot) === project,
  );
  return {
    title: (
      <BreadcrumbTrail
        crumbs={[
          {
            label: projectDisplayName(customerScope.organization),
            onNavigate: onBackToProjects,
          },
          { label: projectDisplayName(project) },
        ]}
      />
    ),
    columnHeader: FOLDER_COLUMN_HEADER,
    itemLabel: FOLDER_ITEM_LABEL,
    rows: folderRows(projectLocations, projectPrefixRoot, onNavigate),
  };
}

/**
 * Build the replacement for the Storage Browser's own locations view.
 *
 * The built-in view lists every location as a prefix across a fixed
 * Folder/Bucket/Permissions column set. The approved design shows one column of
 * named folders, so the view is rebuilt from `useView("Locations")` state while
 * reusing the search, loading and message controls Amplify composes it from.
 * A customer with several projects first chooses one, then sees its folders.
 */
export function createFolderListingView(
  useView: UseView,
  LocationsView: StorageBrowserType["LocationsView"],
  /** Set only for a customer, who browses their own projects; staff span every project. */
  customerScope: CustomerScope | undefined,
  projectPrefixRoot: string,
  /** Set only for a customer with several projects, who must choose one first. */
  projectSelection?: ProjectSelection,
): () => JSX.Element | null {
  function FolderListingView(): JSX.Element | null {
    const locationsState = useView("Locations");
    const [filterText, setFilterText] = useState("");
    const selectedProject = useSelectedProject(projectSelection);
    const onlyLocation = soleLocation(locationsState);
    const hasEnteredOnlyLocation = useRef(false);
    const { onNavigate } = locationsState;

    useEffect(() => {
      if (!onlyLocation || hasEnteredOnlyLocation.current) {
        return;
      }
      hasEnteredOnlyLocation.current = true;
      onNavigate(onlyLocation);
    }, [onlyLocation, onNavigate]);

    if (onlyLocation) {
      return null;
    }

    function selectProject(project: string | undefined): void {
      setFilterText("");
      projectSelection?.select(project);
    }

    function listingContent(): ListingContent {
      if (projectSelection && customerScope) {
        return selectedProject
          ? projectFoldersContent(
              customerScope,
              selectedProject,
              locationsState.pageItems,
              projectPrefixRoot,
              onNavigate,
              () => {
                selectProject(undefined);
              },
            )
          : projectListContent(customerScope, selectProject);
      }
      return {
        title: pageTitleFor(customerScope),
        columnHeader: FOLDER_COLUMN_HEADER,
        itemLabel: FOLDER_ITEM_LABEL,
        rows: folderRows(
          locationsState.pageItems,
          projectPrefixRoot,
          onNavigate,
        ),
      };
    }

    const content = listingContent();
    const visibleRows = content.rows.filter(({ displayName }) =>
      matchesFilter(displayName, filterText),
    );

    return (
      <LocationsView.Provider
        {...locationsState}
        searchQuery={filterText}
        onSearchQueryChange={setFilterText}
        onSearch={() => undefined}
        onSearchClear={() => {
          setFilterText("");
        }}
        onRefresh={() => {
          setFilterText("");
          locationsState.onRefresh();
        }}
      >
        <ListingLayout
          controls={
            <>
              <ListingPagination
                hasNextPage={locationsState.hasNextPage}
                highestPageVisited={locationsState.highestPageVisited}
                itemLabel={content.itemLabel}
                onPaginate={locationsState.onPaginate}
                page={locationsState.page}
                pageItemCount={
                  filterText === "" ? content.rows.length : undefined
                }
              />
              <LocationsView.Refresh />
            </>
          }
          message={<LocationsView.Message />}
          search={<LocationsView.Search />}
          title={content.title}
        >
          <View
            borderColor="border.primary"
            borderRadius="large"
            borderStyle="solid"
            borderWidth="1px"
            flex="1"
            minHeight="0"
            overflow="auto"
          >
            <Table className="evido-folder-listing__table" highlightOnHover>
              <TableHead>
                <TableRow>
                  <TableCell as="th">{content.columnHeader}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map(({ key, displayName, onOpen }) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Button
                        className="evido-folder-listing__folder"
                        onClick={onOpen}
                        variation="link"
                      >
                        {displayName}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </View>
          <LocationsView.LoadingIndicator />
        </ListingLayout>
      </LocationsView.Provider>
    );
  }

  return FolderListingView;
}
